import { useRouter } from 'next/router';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { IntlProvider, ReactIntlErrorCode, useIntl } from 'react-intl';

import { useScribeEnableRtlFlagSource } from '@/app-shell/flags/shell-flags';
import { useOsLocale } from '@/hooks/useOsLocale';
import { useQueryUserLanguage } from '@/hooks/useQueryUserLanguage';
import { configureIntercomLocale } from '@/lib/Intercom';
import { usePseudoLocMessages } from '@/providers/hooks/usePseudoLocMessages';
import { OsLocaleContextProvider } from '@/providers/os-locale-context';
import {
  localeMessageLoaders,
  toIntlMessages,
  type LocaleMessages,
} from '@/utils/i18n/localeMessageLoaders';
import {
  DEFAULT_LOCALE,
  getSupportedLocaleOrDefault,
  SupportedLocale,
} from '@/utils/i18n/supportedLocales';
import { logger } from '@/utils/logger/logger';
import { setCurrentIntl } from '@heidi/runtime/i18n';
import { getDocumentTextDirection } from '@heidi/utils/i18n';

/**
 * Mirrors the active `IntlShape` into the module-level intl bridge so
 * non-React modules can format message descriptors as plain strings
 * (`formatMessageOutsideReact`).
 */
const CurrentIntlBridge = () => {
  const intl = useIntl();
  useEffect(() => {
    setCurrentIntl(intl);
  }, [intl]);
  return null;
};

/** English copy comes from `defaultMessage` on descriptors; no catalog bundle. */
const emptyLocaleMessages = {} as LocaleMessages;

const emptyIntlMessages: Record<string, string> = {};

const messageCache = new Map<SupportedLocale, LocaleMessages>();

const pseudoLocLog = '[intl:pseudoloc]';

interface HeidiIntlProviderProps {
  children: ReactNode;
  /**
   * When false (e.g. production), `en-psaccent` is treated as English and the
   * pseudolocalisation pipeline is not run.
   */
  isPseudolocaleFeatureEnabled?: boolean;
  /**
   * Opt-in for pre-auth surfaces with no Next.js i18n routing (`router.locale`
   * is always `en` there): falls back to the OS/browser locale
   * (`navigator.language`) before `router.locale`, so they render in the user's
   * language on first paint instead of English. Lower precedence than
   * `userLanguageSetting`, so a signed-in user's saved language still wins.
   */
  fallbackToOsLocale?: boolean;
  /**
   * Auxiliary WebViews opt out because mutating `<html>` during startup can
   * prevent their content-ready handshake from completing.
   */
  syncDocumentHtmlAttributes?: boolean;
}

export const HeidiIntlProvider = ({
  children,
  isPseudolocaleFeatureEnabled = false,
  fallbackToOsLocale = false,
  syncDocumentHtmlAttributes = true,
}: HeidiIntlProviderProps) => {
  const router = useRouter();
  // TODO: Locale should come from the server side instead
  const userLanguageSetting = useQueryUserLanguage();
  // The one reactive read; published to every consumer below via context.
  const isRtlLocaleFeatureEnabled = useScribeEnableRtlFlagSource();
  const osLocale = useOsLocale();
  const routerLocale = getSupportedLocaleOrDefault(router.locale, {
    isRtlLocaleFeatureEnabled,
  });
  const shouldUsePseudolocaleRouteOverride =
    isPseudolocaleFeatureEnabled && routerLocale === SupportedLocale.EnPs;
  const rawLocale = shouldUsePseudolocaleRouteOverride
    ? SupportedLocale.EnPs
    : getSupportedLocaleOrDefault(
        userLanguageSetting ??
          (fallbackToOsLocale ? osLocale : undefined) ??
          router.locale,
        { isRtlLocaleFeatureEnabled }
      );
  const activeLocale =
    isPseudolocaleFeatureEnabled || rawLocale !== SupportedLocale.EnPs
      ? rawLocale
      : SupportedLocale.En;

  const [messages, setMessages] = useState<LocaleMessages>(
    () => messageCache.get(activeLocale) ?? emptyLocaleMessages
  );
  const pseudoLocAstMessages = usePseudoLocMessages(activeLocale);

  useEffect(() => {
    configureIntercomLocale(activeLocale);
  }, [activeLocale]);

  /**
   * Keeps `<html lang>` / `dir` in sync with the active app locale.
   *
   * `_document` renders them from the **route** locale on the SSR pass (`/en/...`
   * → `lang="en"`), but the active locale can differ — e.g. an Arabic account
   * language. This effect overrides the SSR attributes after hydration and
   * re-syncs on every locale change (sidebar position and Tailwind `rtl:`
   * variants key off html `dir`).
   *
   * The Next.js pages router also re-writes `document.documentElement.lang`
   * from the route locale on every route state change — including the
   * hydration-time query update, where `routeChangeComplete` never fires
   * (`next/dist/shared/lib/router/router.js`). A MutationObserver re-applies
   * the override whenever anything clobbers `lang`; it only writes when the
   * value actually differs, so our own writes can't loop.
   */
  useEffect(() => {
    if (!syncDocumentHtmlAttributes) return;
    const html = document.documentElement;
    const expectedLang = activeLocale;
    const expectedDir = getDocumentTextDirection(activeLocale, {
      isRtlLocaleFeatureEnabled,
    });

    const applyDocumentHtmlAttributes = () => {
      if (html.getAttribute('lang') !== expectedLang) {
        html.setAttribute('lang', expectedLang);
      }
      if (html.getAttribute('dir') !== expectedDir) {
        html.setAttribute('dir', expectedDir);
      }
    };

    applyDocumentHtmlAttributes();

    const observer = new MutationObserver(() => {
      applyDocumentHtmlAttributes();
      // Discard records created by the corrective writes above so they do not
      // schedule a second callback that can only confirm the expected values.
      observer.takeRecords();
    });

    observer.observe(html, {
      attributes: true,
      attributeFilter: ['lang', 'dir'],
    });

    return () => {
      observer.disconnect();
    };
  }, [activeLocale, isRtlLocaleFeatureEnabled, syncDocumentHtmlAttributes]);

  // Layout phase on the client so synchronous `setMessages` flushes before the pseudolocalisation
  // `useEffect` runs (otherwise both see the same stale `messages` snapshot after locale switches).
  // NOTE: Revert from useEffect to useIsomorphicLayoutEffect forces React to do an extra render+commit cycle before paint.
  // Since IntlProvider wraps the whole app, that pushes back Auth.tsx's useEffect[setToken],
  // which widens an existing race where queries fire with the stale rehydrated token before the fresh Kinde token gets written into Redux.
  useEffect(() => {
    if (activeLocale === SupportedLocale.En || activeLocale === SupportedLocale.EnPs) {
      logger.debug(pseudoLocLog, {
        event: 'locale_messages_branch',
        activeLocale,
        branch:
          activeLocale === SupportedLocale.EnPs
            ? 'en_ps_empty_catalog'
            : 'en_empty_catalog',
      });
      setMessages(emptyLocaleMessages);
      return;
    }

    const cached = messageCache.get(activeLocale);
    if (cached) {
      logger.debug(pseudoLocLog, {
        event: 'locale_messages_branch',
        activeLocale,
        branch: 'memory_cache',
        keyCount: Object.keys(cached).length,
      });
      setMessages(cached);
      return;
    }

    logger.debug(pseudoLocLog, {
      event: 'locale_messages_branch',
      activeLocale,
      branch: 'async_fetch',
    });
    setMessages(emptyLocaleMessages);

    let cancelled = false;

    const loadMessages = async () => {
      try {
        const loader = localeMessageLoaders[activeLocale];
        if (!loader) {
          logger.debug(pseudoLocLog, {
            event: 'locale_messages_no_loader',
            activeLocale,
          });
          return;
        }

        const mod = await loader();
        const loaded = mod.default ?? mod;

        if (!cancelled) {
          messageCache.set(activeLocale, loaded);
          setMessages(loaded);
          logger.debug(pseudoLocLog, {
            event: 'locale_messages_loaded',
            activeLocale,
            keyCount: Object.keys(loaded).length,
          });
        } else {
          logger.debug(pseudoLocLog, {
            event: 'locale_messages_load_cancelled',
            activeLocale,
          });
        }
      } catch (error) {
        logger.error('Failed to load react-intl messages', error);
      }
    };

    void loadMessages();

    return () => {
      cancelled = true;
    };
  }, [activeLocale, isPseudolocaleFeatureEnabled]);

  const intlMessages = useMemo(() => {
    if (activeLocale === SupportedLocale.En || activeLocale === SupportedLocale.EnPs) {
      return emptyIntlMessages;
    }
    return toIntlMessages(messages);
  }, [activeLocale, messages]);

  const providerMessages = useMemo(() => {
    if (
      activeLocale === SupportedLocale.EnPs &&
      pseudoLocAstMessages &&
      Object.keys(pseudoLocAstMessages).length > 0
    ) {
      return pseudoLocAstMessages;
    }
    return intlMessages;
  }, [activeLocale, intlMessages, pseudoLocAstMessages]);

  return (
    <IntlProvider
      locale={activeLocale}
      defaultLocale={DEFAULT_LOCALE}
      messages={providerMessages}
      onError={(err) => {
        if (err.code === ReactIntlErrorCode.MISSING_TRANSLATION) return;
        logger.error('react-intl error', err);
      }}
    >
      {/*
        SCR-5924: hoist the OS/browser locale here (computed once above) so the
        ~1,100 `useTranslations()` consumers read it from context instead of each
        calling `useOsLocale()` on mount.
      */}
      <OsLocaleContextProvider
        value={osLocale}
        isRtlLocaleFeatureEnabled={isRtlLocaleFeatureEnabled}
      >
        <CurrentIntlBridge />
        {children}
      </OsLocaleContextProvider>
    </IntlProvider>
  );
};
